/**
 * RadarChart Utility Functions
 */

import {
  Point,
  ScorePoint,
  RadarGroup,
  ComputedGroup,
  ComputedSector,
} from "./types";
import { orderBy } from "lodash";

// Chart constants
export const CENTER_X = 50;
export const CENTER_Y = 50;
export const CHART_RADIUS = 30;
export const SECTOR_LABEL_RADIUS = CHART_RADIUS + 4;
export const GROUP_LABEL_RADIUS = CHART_RADIUS + 13;
export const AXIS_LINE_INNER_RADIUS = CHART_RADIUS - 8;
export const AXIS_LINE_OUTER_RADIUS = CHART_RADIUS + 8;
// Axis labels on same radius as group labels
export const AXIS_LABEL_RADIUS = GROUP_LABEL_RADIUS;

/**
 * Convert polar coordinates to cartesian
 */
export const polarToCartesian = (
  angle: number,
  radius: number,
  cx: number = CENTER_X,
  cy: number = CENTER_Y
): Point => ({
  x: cx + radius * Math.cos((Math.PI * angle) / 180),
  y: cy + radius * Math.sin((Math.PI * angle) / 180),
});

/**
 * Check if angle is in bottom half (text should be flipped)
 * In SVG: 0° = right, 90° = bottom, 180° = left, 270° = top
 * Bottom half is 0-180 degrees
 */
export const shouldFlipText = (angle: number): boolean => {
  const normalized = ((angle % 360) + 360) % 360;
  return normalized > 0 && normalized < 180;
};

/**
 * Generate arc path for text
 */
export const generateTextArcPath = (
  startAngle: number,
  endAngle: number,
  radius: number,
  flip: boolean
): string => {
  const p1 = polarToCartesian(startAngle, radius);
  const p2 = polarToCartesian(endAngle, radius);

  if (flip) {
    // Draw from end to start for flipped text
    return `M${p2.x.toFixed(2)} ${p2.y.toFixed(
      2
    )} A${radius} ${radius} 0 0 0 ${p1.x.toFixed(2)} ${p1.y.toFixed(2)}`;
  }
  return `M${p1.x.toFixed(2)} ${p1.y.toFixed(
    2
  )} A${radius} ${radius} 0 0 1 ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`;
};

/**
 * Generate arc path for rim coloring
 */
export const generateRimArcPath = (
  startAngle: number,
  endAngle: number,
  radius: number
): string => {
  const p1 = polarToCartesian(startAngle, radius);
  const p2 = polarToCartesian(endAngle, radius);
  return `M${p1.x.toFixed(2)} ${p1.y.toFixed(
    2
  )} A${radius} ${radius} 0 0 1 ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`;
};

/**
 * Generate sector wedge path (for hover/click area)
 */
export const generateSectorPath = (
  startAngle: number,
  endAngle: number,
  radius: number
): string => {
  const p1 = polarToCartesian(startAngle, radius);
  const p2 = polarToCartesian(endAngle, radius);
  return `M${CENTER_X} ${CENTER_Y} L${p1.x.toFixed(2)} ${p1.y.toFixed(
    2
  )} A${radius} ${radius} 0 0 1 ${p2.x.toFixed(2)} ${p2.y.toFixed(2)} z`;
};

/**
 * Generate crosshair axis line
 */
export const generateAxisLine = (
  angle: number,
  innerRadius: number,
  outerRadius: number
): string => {
  const p1 = polarToCartesian(angle, innerRadius);
  const p2 = polarToCartesian(angle, outerRadius);
  return `M${p1.x.toFixed(2)} ${p1.y.toFixed(2)} L${p2.x.toFixed(
    2
  )} ${p2.y.toFixed(2)}`;
};

/**
 * Compute groups with angular positions
 * Each group is exactly 90 degrees (quadrant)
 * Sectors within group are distributed proportionally
 */
export const computeGroups = (groups: RadarGroup[]): ComputedGroup[] => {
  if (groups.length !== 4) {
    console.warn("RadarChart expects exactly 4 groups");
  }

  // Collect all sectors for ranking
  const allSectors = groups.flatMap((g, gi) =>
    g.sectors.map((s) => ({ ...s, groupIndex: gi }))
  );
  const sortedByScore = orderBy(allSectors, "score", "desc");

  const computedGroups: ComputedGroup[] = [];
  let currentAngle = 0; // Start from right (0°)

  groups.forEach((group, groupIndex) => {
    const groupAngle = 90; // Each group is exactly 90°
    const groupStartAngle = currentAngle;
    const groupEndAngle = currentAngle + groupAngle;
    const groupMidAngle = (groupStartAngle + groupEndAngle) / 2;

    const sectorCount = group.sectors.length;
    const sectorAngle = sectorCount > 0 ? groupAngle / sectorCount : groupAngle;

    let sectorCurrentAngle = groupStartAngle;
    const computedSectors: ComputedSector[] = group.sectors.map((sector) => {
      const startAngle = sectorCurrentAngle;
      const endAngle = sectorCurrentAngle + sectorAngle;
      const midAngle = (startAngle + endAngle) / 2;
      sectorCurrentAngle = endAngle;

      // Determine color ranking
      const rankIndex = sortedByScore.findIndex((s) => s.id === sector.id);
      let colorRank: ComputedSector["colorRank"] = "neutral";

      if (rankIndex === 0) colorRank = "top1";
      else if (rankIndex === 1) colorRank = "top2";
      else if (rankIndex === 2) colorRank = "top3";
      else if (rankIndex === sortedByScore.length - 1) colorRank = "bottom1";
      else if (rankIndex === sortedByScore.length - 2) colorRank = "bottom2";
      else if (rankIndex === sortedByScore.length - 3) colorRank = "bottom3";

      return {
        ...sector,
        startAngle,
        endAngle,
        midAngle,
        groupIndex,
        colorRank,
      };
    });

    computedGroups.push({
      ...group,
      startAngle: groupStartAngle,
      endAngle: groupEndAngle,
      midAngle: groupMidAngle,
      computedSectors,
    });

    currentAngle = groupEndAngle;
  });

  return computedGroups;
};

/**
 * Calculate score coordinates for a data layer
 */
export const calculateScorePoints = (
  scores: Record<string | number, number>,
  computedGroups: ComputedGroup[]
): ScorePoint[] => {
  const points: ScorePoint[] = [];

  computedGroups.forEach((group) => {
    group.computedSectors.forEach((sector) => {
      const score = scores[sector.id] ?? 0;
      const normalizedScore = Math.max(1, Math.min(100, score)); // Min 1 for visibility
      const radius = (normalizedScore / 100) * CHART_RADIUS;
      const point = polarToCartesian(sector.midAngle, radius);

      points.push({
        x: point.x,
        y: point.y,
        score: normalizedScore,
        sectorId: sector.id,
      });
    });
  });

  return points;
};

/**
 * Generate blob path with slight curves
 * Uses quadratic bezier for smoother but not overly smooth curves
 */
export const generateBlobPath = (points: ScorePoint[]): string => {
  if (points.length === 0) return "";

  const tension = 0.12; // Lower = sharper, Higher = smoother

  const getControlPoints = (
    prev: Point,
    curr: Point,
    next: Point
  ): { cp1: Point; cp2: Point } => {
    const d1 = Math.sqrt((curr.x - prev.x) ** 2 + (curr.y - prev.y) ** 2);
    const d2 = Math.sqrt((next.x - curr.x) ** 2 + (next.y - curr.y) ** 2);

    const fa = (tension * d1) / (d1 + d2);
    const fb = tension - fa;

    return {
      cp1: {
        x: curr.x - fa * (next.x - prev.x),
        y: curr.y - fa * (next.y - prev.y),
      },
      cp2: {
        x: curr.x + fb * (next.x - prev.x),
        y: curr.y + fb * (next.y - prev.y),
      },
    };
  };

  // Generate control points for each vertex
  const controlPoints = points.map((point, i) => {
    const prev = points[(i - 1 + points.length) % points.length];
    const next = points[(i + 1) % points.length];
    return getControlPoints(prev, point, next);
  });

  // Build path
  let path = `M ${points[0].x.toFixed(2)} ${points[0].y.toFixed(2)}`;

  for (let i = 0; i < points.length; i++) {
    const nextIndex = (i + 1) % points.length;
    const curr = points[i];
    const next = points[nextIndex];
    const currCp = controlPoints[i];
    const nextCp = controlPoints[nextIndex];

    path += ` C ${currCp.cp2.x.toFixed(2)} ${currCp.cp2.y.toFixed(
      2
    )}, ${nextCp.cp1.x.toFixed(2)} ${nextCp.cp1.y.toFixed(2)}, ${next.x.toFixed(
      2
    )} ${next.y.toFixed(2)}`;
  }

  return path;
};

/**
 * Calculate centroid of points (for indicator positioning)
 */
export const calculateCentroid = (points: ScorePoint[]): Point => {
  if (points.length === 0) return { x: CENTER_X, y: CENTER_Y };

  const sumX = points.reduce((sum, p) => sum + p.x, 0);
  const sumY = points.reduce((sum, p) => sum + p.y, 0);

  return {
    x: sumX / points.length,
    y: sumY / points.length,
  };
};

/**
 * Get rim color class based on ranking
 */
export const getRimColorClass = (rank: ComputedSector["colorRank"]): string => {
  switch (rank) {
    case "top1":
      return "stroke-green-500";
    case "top2":
      return "stroke-green-300";
    case "top3":
      return "stroke-green-200";
    case "bottom1":
      return "stroke-orange-500";
    case "bottom2":
      return "stroke-orange-300";
    case "bottom3":
      return "stroke-orange-200";
    default:
      return "stroke-gray-300";
  }
};
